import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Callback
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);

      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(
            `${API}/auth/google/session`,
            {},
            {
              headers: { 'X-Session-ID': sessionId },
              withCredentials: true
            }
          );
          setUser(response.data);
          navigate('/dashboard', { replace: true, state: { user: response.data } });
        } catch (error) {
          console.error('Google auth error:', error);
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/dashboard', { replace: true });
      }
    };

    processSession();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-muted-foreground">Logowanie...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate('/login', { replace: true });
    }
  }, [user, loading, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return null;
  }

  return children;
};

// Pages
import HomePage from "@/pages/HomePage";
import SearchPage from "@/pages/SearchPage";
import BusinessPage from "@/pages/BusinessPage";
import LandingPreviewPage from "@/pages/LandingPreviewPage";
import DashboardPage from "@/pages/DashboardPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import PricingPage from "@/pages/PricingPage";
import MockPaymentPage from "@/pages/MockPaymentPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import AdminLayout from "@/pages/AdminLayout";
import AdminBusinessesPage from "@/pages/AdminBusinessesPage";
import AdminPaymentsPage from "@/pages/AdminPaymentsPage";
import AdminLeadsPage from "@/pages/AdminLeadsPage";
import AdminAnalyticsPage from "@/pages/AdminAnalyticsPage";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import AdminLegalPage from "@/pages/AdminLegalPage";
import LegalPage from "@/pages/LegalPage";
import MenuScannerPage from "@/pages/MenuScannerPage";
import SeoPage from "@/pages/SeoPage";
import SocialCalendarPage from "@/pages/SocialCalendarPage";
import SubdomainLandingPage from "@/pages/SubdomainLandingPage";
import PageBuilderPage from "@/pages/PageBuilderPage";
import WelcomePage from "@/pages/WelcomePage";
import NotFoundPage from "@/pages/NotFoundPage";

// Detect subdomain OR custom domain: anything other than the apex/www host
// triggers a dedicated single-business landing instead of the lokalnie.pro
// app shell.
// Examples that ARE subdomains: fizjo.lokalnie.pro, salon-bella.lokalnie.dev.sorto.ai
// Examples that ARE custom domains: twojafirma.pl (Premium, own domain)
// Reserved (NOT subdomains): the apex itself, www, api, admin (future).
function detectSubdomain() {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname;
  const APEX_HOSTS = ["lokalnie.pro", "lokalnie.dev.sorto.ai", "localhost", "127.0.0.1"];
  const RESERVED = new Set(["www", "api", "admin"]);
  if (APEX_HOSTS.includes(host)) return null;
  for (const apex of APEX_HOSTS) {
    if (host.endsWith(`.${apex}`)) {
      const sub = host.slice(0, -1 - apex.length).split(".")[0];
      if (!sub || RESERVED.has(sub)) return null;
      return { type: "subdomain", value: sub };
    }
  }
  // Nie pasuje do zadnego znanego apexu -> traktowane jako pelna wlasna domena
  // klienta Premium (np. twojafirma.pl lub www.twojafirma.pl — dokladnie tak,
  // jak wlasciciel wpisal ja w panelu), routowana przez Traefik na ten sam
  // frontend (patrz regenerate_traefik_subdomain_config w backendzie).
  return { type: "custom_domain", value: host };
}

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Subdomain/custom-domain mode — full single-business landing, no lokalnie.pro chrome
  const site = detectSubdomain();
  if (site?.type === "subdomain") {
    return <SubdomainLandingPage subdomain={site.value} />;
  }
  if (site?.type === "custom_domain") {
    return <SubdomainLandingPage customDomain={site.value} />;
  }

  // Check for session_id in URL fragment synchronously
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/szukaj" element={<SearchPage />} />
      <Route path="/firma/:businessId" element={<BusinessPage />} />
      <Route path="/witaj/:token" element={<WelcomePage />} />
      <Route path="/landing/:businessId" element={<LandingPreviewPage />} />
      <Route path="/builder/:businessId" element={
        <ProtectedRoute>
          <PageBuilderPage />
        </ProtectedRoute>
      } />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/rejestracja" element={<RegisterPage />} />
      <Route path="/zapomnialem-hasla" element={<ForgotPasswordPage />} />
      <Route path="/reset-hasla/:token" element={<ResetPasswordPage />} />
      <Route path="/weryfikacja-email/:token" element={<VerifyEmailPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/payment/mock" element={<MockPaymentPage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/regulamin" element={<LegalPage docType="terms" title="Regulamin" />} />
      <Route path="/polityka-prywatnosci" element={<LegalPage docType="privacy" title="Polityka Prywatności" />} />
      <Route path="/umowa-powierzenia-danych" element={<LegalPage docType="dpa" title="Umowa Powierzenia Danych (DPA)" />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route path="businesses" element={<AdminBusinessesPage />} />
        <Route path="payments" element={<AdminPaymentsPage />} />
        <Route path="leads" element={<AdminLeadsPage />} />
        <Route path="analytics" element={<AdminAnalyticsPage />} />
        <Route path="stats" element={<AdminBusinessesPage />} />
        <Route path="menu-scanner" element={<MenuScannerPage />} />
        <Route path="seo" element={<SeoPage />} />
        <Route path="social" element={<SocialCalendarPage />} />
        <Route path="settings" element={<AdminSettingsPage />} />
        <Route path="legal" element={<AdminLegalPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App">
          <AppRouter />
          <Toaster position="top-right" richColors />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
